// Phase 12 — Form Builder (palette / preview with reorder / config panel)
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, Type, AlignLeft, Hash, ChevronDown, Calendar as CalIcon, Camera, Images,
  MapPin, PenLine, CheckSquare, Star, Minus, Trash2, GripVertical, CheckCircle2, Rocket,
  Info, Save, Target as TargetIcon, X,
} from "lucide-react";
import {
  DndContext, closestCenter, PointerSensor, useSensor, useSensors,
} from "@dnd-kit/core";
import {
  SortableContext, verticalListSortingStrategy, useSortable, arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { SearchableSelect } from "@/components/ui/searchable-select";

const PALETTE = [
  { code: "text",           label: "Short Text",    icon: Type,        default_label: "Text Field" },
  { code: "textarea",       label: "Long Text",     icon: AlignLeft,   default_label: "Notes" },
  { code: "number",         label: "Number",        icon: Hash,        default_label: "Number" },
  { code: "dropdown",       label: "Dropdown",      icon: ChevronDown, default_label: "Select" },
  { code: "multiselect",    label: "Multi Select",  icon: ChevronDown, default_label: "Choose Multiple" },
  { code: "date",           label: "Date",          icon: CalIcon,     default_label: "Date" },
  { code: "photo",          label: "Photo",         icon: Camera,      default_label: "Photo" },
  { code: "photo_pair",     label: "Before / After Photos", icon: Images, default_label: "Before / After" },
  { code: "gps",            label: "GPS",           icon: MapPin,      default_label: "GPS Location" },
  { code: "signature",      label: "Signature",     icon: PenLine,     default_label: "Signature" },
  { code: "checkbox",       label: "Checkbox",      icon: CheckSquare, default_label: "Yes / No" },
  { code: "rating",         label: "Rating (1-5)",  icon: Star,        default_label: "Rating" },
  { code: "section_divider",label: "Section",       icon: Minus,       default_label: "Section" },
];

function fieldKeyFromLabel(label) {
  return String(label || "field").toLowerCase()
    .replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "").slice(0, 60) || "field";
}

export default function FormBuilder() {
  const { form_id } = useParams();
  const nav = useNavigate();
  const [form, setForm] = useState(null);
  const [fields, setFields] = useState([]);
  const [selectedFieldId, setSelectedFieldId] = useState(null);
  const [publishOpen, setPublishOpen] = useState(false);
  const [targetingOpen, setTargetingOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const selectedField = useMemo(
    () => fields.find(f => f.id === selectedFieldId) || null,
    [fields, selectedFieldId]
  );

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  async function load() {
    try {
      const { data } = await api.get(`/activity-forms/${form_id}`);
      setForm(data);
      setFields(data.fields || []);
    } catch (e) { toast.error(formatApiError(e)); }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [form_id]);

  async function addField(paletteItem) {
    const baseKey = fieldKeyFromLabel(paletteItem.default_label);
    let field_key = baseKey; let i = 1;
    while (fields.some(f => f.field_key === field_key)) { field_key = `${baseKey}_${++i}`; }
    try {
      const body = {
        field_key, label: paletteItem.default_label, field_type: paletteItem.code,
        required: false, order_index: fields.length,
        options: (paletteItem.code === "dropdown" || paletteItem.code === "multiselect")
          ? [{ code: "yes", label: "Yes" }, { code: "no", label: "No" }] : null,
      };
      const { data } = await api.post(`/activity-forms/${form_id}/fields`, body);
      const next = [...fields, data];
      setFields(next);
      setSelectedFieldId(data.id);
    } catch (e) { toast.error(formatApiError(e)); }
  }

  async function persistFieldEdit(field, patch) {
    // optimistic
    setFields((prev) => prev.map(f => (f.id === field.id ? { ...f, ...patch } : f)));
    try {
      await api.patch(`/activity-forms/${form_id}/fields/${field.id}`, patch);
    } catch (e) {
      toast.error(formatApiError(e));
      load();
    }
  }

  async function deleteField(field) {
    if (!window.confirm(`Delete field '${field.label}'?`)) return;
    try {
      await api.delete(`/activity-forms/${form_id}/fields/${field.id}`);
      setFields((prev) => prev.filter(f => f.id !== field.id));
      if (selectedFieldId === field.id) setSelectedFieldId(null);
    } catch (e) { toast.error(formatApiError(e)); }
  }

  function onDragEnd(evt) {
    const { active, over } = evt;
    if (!over || active.id === over.id) return;
    const oldIdx = fields.findIndex(f => f.id === active.id);
    const newIdx = fields.findIndex(f => f.id === over.id);
    const reordered = arrayMove(fields, oldIdx, newIdx);
    setFields(reordered);
    api.post(`/activity-forms/${form_id}/reorder-fields`, {
      field_ids: reordered.map(f => f.id),
    }).catch((e) => { toast.error(formatApiError(e)); load(); });
  }

  async function saveDraft() {
    setBusy(true);
    try {
      await api.patch(`/activity-forms/${form_id}`, {
        name: form.name, description: form.description || null,
        category: form.category, applicable_role_codes: form.applicable_role_codes,
        applicable_zone_ids: form.applicable_zone_ids,
        applicable_beat_ids: form.applicable_beat_ids,
        target_retailer_ids: form.target_retailer_ids || [],
        target_distributor_ids: form.target_distributor_ids || [],
        target_user_ids: form.target_user_ids || [],
        target_sub_zone_ids: form.target_sub_zone_ids || [],
        duplicate_policy: form.duplicate_policy,
        show_at_call: !!form.show_at_call, call_mandatory: !!form.call_mandatory,
        call_mandatory_when: form.call_mandatory_when || "both",
        status: "draft",
      });
      toast.success("Draft saved");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  }

  async function saveTargeting(patch) {
    setBusy(true);
    try {
      await api.patch(`/activity-forms/${form_id}`, patch);
      toast.success("Saved");
      setTargetingOpen(false);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  }

  async function publish() {
    setBusy(true);
    try {
      const { data } = await api.post(`/activity-forms/${form_id}/publish`, {});
      toast.success(`Published — v${data.version}`);
      setPublishOpen(false);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  }

  if (!form) return <div className="p-6 text-neutral-500">Loading…</div>;

  return (
    <div className="min-h-screen bg-neutral-50 flex flex-col" data-testid="form-builder-page">
      {/* Header */}
      <div className="bg-white border-b px-6 py-4 flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => nav("/admin/forms")}
                data-testid="builder-back">
          <ArrowLeft className="w-4 h-4 mr-1" /> Back to Forms
        </Button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-mono text-xs text-neutral-400">{form.code}</span>
            <Badge variant="outline" className={
              form.status === "published"
                ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                : "bg-amber-50 text-amber-800 border-amber-200"
            }>
              {form.status} · v{form.version}
            </Badge>
          </div>
          <h1 className="text-lg font-semibold truncate">{form.name}</h1>
        </div>
        <Button variant="outline" size="sm" onClick={() => setTargetingOpen(true)} disabled={busy}
                data-testid="builder-targeting-btn">
          <TargetIcon className="w-4 h-4 mr-1" /> Call &amp; targeting
          {form.show_at_call ? (
            <Badge variant="outline" className="ml-1 h-5 text-[10px] bg-sky-50 border-sky-200 text-sky-700">
              {form.call_mandatory ? "In call · mandatory" : "In call"}
            </Badge>
          ) : null}
          {(form.target_retailer_ids?.length || form.target_distributor_ids?.length || form.target_user_ids?.length || form.target_sub_zone_ids?.length) ? (
            <Badge variant="outline" className="ml-1 h-5 text-[10px] bg-emerald-50 border-emerald-200 text-emerald-700">
              {(form.target_retailer_ids?.length || 0) + (form.target_distributor_ids?.length || 0) + (form.target_user_ids?.length || 0) + (form.target_sub_zone_ids?.length || 0)}
            </Badge>
          ) : null}
        </Button>
        <Button variant="outline" size="sm" onClick={saveDraft} disabled={busy}
                data-testid="builder-save-draft">
          <Save className="w-4 h-4 mr-1" /> Save Draft
        </Button>
        <Button size="sm" onClick={() => setPublishOpen(true)}
                disabled={busy || fields.length === 0}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="builder-publish">
          <Rocket className="w-4 h-4 mr-1" /> Publish
        </Button>
      </div>

      {/* 3-column layout: palette / preview / config */}
      <div className="flex-1 grid grid-cols-12 gap-4 p-4">
        {/* LEFT — palette */}
        <aside className="col-span-3 bg-white border rounded-lg p-4 h-fit sticky top-4"
               data-testid="builder-palette">
          <h3 className="font-medium text-sm text-neutral-500 uppercase tracking-wider mb-3">
            Fields
          </h3>
          <div className="grid grid-cols-2 gap-2">
            {PALETTE.map((p) => {
              const Icon = p.icon;
              return (
                <button
                  key={p.code}
                  type="button"
                  data-testid={`palette-add-${p.code}`}
                  onClick={() => addField(p)}
                  className="flex flex-col items-center justify-center gap-1 p-3 rounded-md border border-neutral-200 hover:border-emerald-500 hover:bg-emerald-50 text-xs transition"
                >
                  <Icon className="w-4 h-4 text-neutral-600" />
                  <span className="text-[11px] text-center leading-tight">{p.label}</span>
                </button>
              );
            })}
          </div>
        </aside>

        {/* CENTER — preview */}
        <section className="col-span-6 bg-white border rounded-lg p-6 min-h-[70vh]"
                 data-testid="builder-preview">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-medium text-sm text-neutral-500 uppercase tracking-wider">
              Preview ({fields.length} fields)
            </h3>
            {fields.length === 0 && (
              <span className="text-xs text-neutral-400 italic">Add fields from the left panel →</span>
            )}
          </div>
          {fields.length === 0 ? (
            <div className="border-2 border-dashed border-neutral-200 rounded-lg p-10 text-center text-neutral-500">
              <Type className="w-8 h-8 mx-auto mb-2 text-neutral-300" />
              <p className="text-sm">No fields yet</p>
              <p className="text-xs mt-1">Click any field in the palette to add it</p>
            </div>
          ) : (
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
              <SortableContext items={fields.map(f => f.id)} strategy={verticalListSortingStrategy}>
                <div className="space-y-2">
                  {fields.map((f) => (
                    <SortablePreviewField key={f.id} field={f}
                      isSelected={selectedFieldId === f.id}
                      onSelect={() => setSelectedFieldId(f.id)}
                      onDelete={() => deleteField(f)} />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          )}
        </section>

        {/* RIGHT — config panel */}
        <aside className="col-span-3 bg-white border rounded-lg p-4 h-fit sticky top-4"
               data-testid="builder-config">
          <h3 className="font-medium text-sm text-neutral-500 uppercase tracking-wider mb-3">
            Field Config
          </h3>
          {!selectedField ? (
            <div className="text-xs text-neutral-400 py-4">
              <Info className="w-4 h-4 inline mr-1" />
              Select a field to edit its config
            </div>
          ) : (
            <FieldConfigPanel field={selectedField} fields={fields}
                              onSave={(patch) => persistFieldEdit(selectedField, patch)} />
          )}
        </aside>
      </div>

      <AlertDialog open={publishOpen} onOpenChange={setPublishOpen}>
        <AlertDialogContent data-testid="publish-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Publish this form?</AlertDialogTitle>
            <AlertDialogDescription>
              {form.status === "published"
                ? `This will make the form live for all matching field users. Version will bump from ${form.version} to ${form.version + 1} if fields changed since last publish.`
                : `This will make the form live for all matching field users. Version will be set to v${form.version || 1}.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="publish-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="publish-confirm-btn" onClick={publish}
                               className="bg-emerald-600 hover:bg-emerald-700">
              <Rocket className="w-4 h-4 mr-1" /> Publish now
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <TargetingDialog open={targetingOpen} onOpenChange={setTargetingOpen}
                       form={form} onSave={saveTargeting} busy={busy} />
    </div>
  );
}


function SortablePreviewField({ field, isSelected, onSelect, onDelete }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: field.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const IconMap = PALETTE.reduce((acc, p) => { acc[p.code] = p.icon; return acc; }, {});
  const Icon = IconMap[field.field_type] || Type;
  const isSection = field.field_type === "section_divider";

  return (
    <div ref={setNodeRef} style={style}
         className={`border rounded-md p-3 cursor-pointer transition ${
           isSelected ? "border-emerald-500 bg-emerald-50/50 ring-2 ring-emerald-100" : "border-neutral-200 bg-white hover:border-neutral-400"
         }`}
         onClick={onSelect}
         data-testid={`preview-field-${field.field_key}`}>
      <div className="flex items-center gap-2">
        <button {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing text-neutral-400 hover:text-neutral-700 -ml-1">
          <GripVertical className="w-4 h-4" />
        </button>
        <Icon className="w-4 h-4 text-neutral-500 shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className={`text-sm font-medium ${isSection ? "text-neutral-500 italic" : ""}`}>{field.label}</span>
            {field.required && <Badge variant="outline" className="text-[10px] bg-red-50 text-red-700 border-red-200">required</Badge>}
            {field.conditional && <Badge variant="outline" className="text-[10px] bg-blue-50 text-blue-700 border-blue-200">conditional</Badge>}
          </div>
          <div className="text-[11px] text-neutral-400 mt-0.5 font-mono">
            {field.field_key} · {field.field_type}
          </div>
        </div>
        <button type="button" onClick={(e) => { e.stopPropagation(); onDelete(); }}
                data-testid={`delete-field-${field.field_key}`}
                className="text-neutral-400 hover:text-red-600 p-1">
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}


function FieldConfigPanel({ field, fields, onSave }) {
  const [label, setLabel] = useState(field.label);
  const [required, setRequired] = useState(!!field.required);
  const [helpText, setHelpText] = useState(field.help_text || "");
  const [options, setOptions] = useState(field.options || []);
  const [validation, setValidation] = useState(field.validation || {});
  const [condEnable, setCondEnable] = useState(!!field.conditional);
  const [condFieldKey, setCondFieldKey] = useState(field.conditional?.when?.field_key || "");
  const [condOp, setCondOp] = useState(field.conditional?.when?.op || "eq");
  const [condValue, setCondValue] = useState(String(field.conditional?.when?.value ?? ""));

  const isSection = field.field_type === "section_divider";
  const hasOptions = field.field_type === "dropdown" || field.field_type === "multiselect";
  const isNumber = field.field_type === "number";
  const isText = field.field_type === "text" || field.field_type === "textarea";

  useEffect(() => {
    setLabel(field.label);
    setRequired(!!field.required);
    setHelpText(field.help_text || "");
    setOptions(field.options || []);
    setValidation(field.validation || {});
    setCondEnable(!!field.conditional);
    setCondFieldKey(field.conditional?.when?.field_key || "");
    setCondOp(field.conditional?.when?.op || "eq");
    setCondValue(String(field.conditional?.when?.value ?? ""));
  }, [field.id]);

  function persist() {
    const patch = {
      label, required, help_text: helpText || null,
      options: hasOptions ? options : null,
      validation: (isNumber || isText) ? validation : null,
      conditional: condEnable && condFieldKey ? {
        when: { field_key: condFieldKey, op: condOp, value: castValue(condValue, fields, condFieldKey) },
      } : null,
    };
    onSave(patch);
    toast.success("Field updated");
  }

  return (
    <div className="space-y-3 text-sm">
      <div>
        <Label>Label</Label>
        <Input data-testid="cfg-label" value={label} onChange={(e) => setLabel(e.target.value)} />
      </div>
      <div className="text-xs text-neutral-500 font-mono">key: {field.field_key}</div>
      {!isSection && (
        <div className="flex items-center justify-between">
          <Label>Required</Label>
          <Switch data-testid="cfg-required" checked={required} onCheckedChange={setRequired} />
        </div>
      )}
      <div>
        <Label>Help text</Label>
        <Textarea data-testid="cfg-help" rows={2} value={helpText}
                  onChange={(e) => setHelpText(e.target.value)} />
      </div>

      {hasOptions && (
        <div>
          <Label>Options</Label>
          <div className="space-y-2 mt-1" data-testid="cfg-options-list">
            {options.map((o, i) => (
              <div key={i} className="flex gap-2">
                <Input placeholder="code" value={o.code}
                       onChange={(e) => setOptions(options.map((x, j) => j === i ? { ...x, code: e.target.value } : x))} />
                <Input placeholder="label" value={o.label}
                       onChange={(e) => setOptions(options.map((x, j) => j === i ? { ...x, label: e.target.value } : x))} />
                <Button size="icon" variant="ghost" onClick={() => setOptions(options.filter((_, j) => j !== i))}>
                  <Trash2 className="w-3 h-3" />
                </Button>
              </div>
            ))}
            <Button size="sm" variant="outline" data-testid="cfg-option-add"
                    onClick={() => setOptions([...options, { code: "", label: "" }])}>
              + Add option
            </Button>
          </div>
        </div>
      )}

      {(isNumber || isText) && (
        <div>
          <Label>Validation</Label>
          <div className="grid grid-cols-2 gap-2 mt-1">
            {isNumber ? (
              <>
                <div>
                  <Label className="text-xs text-neutral-500">Min</Label>
                  <Input type="number" value={validation.min ?? ""} onChange={(e) => setValidation({ ...validation, min: e.target.value === "" ? undefined : Number(e.target.value) })} />
                </div>
                <div>
                  <Label className="text-xs text-neutral-500">Max</Label>
                  <Input type="number" value={validation.max ?? ""} onChange={(e) => setValidation({ ...validation, max: e.target.value === "" ? undefined : Number(e.target.value) })} />
                </div>
              </>
            ) : (
              <>
                <div>
                  <Label className="text-xs text-neutral-500">Min length</Label>
                  <Input type="number" value={validation.min_length ?? ""} onChange={(e) => setValidation({ ...validation, min_length: e.target.value === "" ? undefined : Number(e.target.value) })} />
                </div>
                <div>
                  <Label className="text-xs text-neutral-500">Max length</Label>
                  <Input type="number" value={validation.max_length ?? ""} onChange={(e) => setValidation({ ...validation, max_length: e.target.value === "" ? undefined : Number(e.target.value) })} />
                </div>
              </>
            )}
          </div>
        </div>
      )}

      <div className="border-t pt-3">
        <div className="flex items-center justify-between mb-2">
          <Label>Conditional visibility</Label>
          <Switch data-testid="cfg-cond-toggle" checked={condEnable} onCheckedChange={setCondEnable} />
        </div>
        {condEnable && (
          <div className="space-y-2">
            <Select value={condFieldKey || "__pick__"} onValueChange={(v) => setCondFieldKey(v === "__pick__" ? "" : v)}>
              <SelectTrigger data-testid="cfg-cond-field"><SelectValue placeholder="Target field" /></SelectTrigger>
              <SelectContent>
                {fields.filter(f => f.id !== field.id && f.field_type !== "section_divider").map(f => (
                  <SelectItem key={f.id} value={f.field_key}>{f.label} ({f.field_key})</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="grid grid-cols-2 gap-2">
              <Select value={condOp} onValueChange={setCondOp}>
                <SelectTrigger data-testid="cfg-cond-op"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="eq">equals</SelectItem>
                  <SelectItem value="ne">not equals</SelectItem>
                  <SelectItem value="gt">greater than</SelectItem>
                  <SelectItem value="lt">less than</SelectItem>
                  <SelectItem value="in">in list</SelectItem>
                </SelectContent>
              </Select>
              <Input data-testid="cfg-cond-value" placeholder="value"
                     value={condValue} onChange={(e) => setCondValue(e.target.value)} />
            </div>
            <p className="text-[11px] text-neutral-400">
              Field will be shown & validated only when the condition is met.
            </p>
          </div>
        )}
      </div>

      <Button className="w-full" onClick={persist} data-testid="cfg-save"
              size="sm">
        <CheckCircle2 className="w-4 h-4 mr-1" /> Apply changes
      </Button>
    </div>
  );
}


function castValue(raw, allFields, keyRef) {
  const target = allFields.find(f => f.field_key === keyRef);
  if (!target) return raw;
  if (target.field_type === "number") {
    const n = Number(raw); return isNaN(n) ? raw : n;
  }
  if (target.field_type === "checkbox") {
    return raw === "true" || raw === true;
  }
  return raw;
}


// Phase 23C — Form targeting dialog
const TARGETING_ROLES = ["SUPER_ADMIN", "MANAGEMENT", "SALES_HEAD", "ZSM", "RSM", "ASM", "SE", "DISTRIBUTOR", "MIS"];

function TargetingDialog({ open, onOpenChange, form, onSave, busy }) {
  const [retailers, setRetailers] = useState([]);
  const [distributors, setDistributors] = useState([]);
  const [users, setUsers] = useState([]);
  const [selRetailers, setSelRetailers] = useState([]);
  const [selDistributors, setSelDistributors] = useState([]);
  const [selUsers, setSelUsers] = useState([]);
  const [selRoles, setSelRoles] = useState([]);
  const [subZones, setSubZones] = useState([]);
  const [selSubZones, setSelSubZones] = useState([]);
  const [showAtCall, setShowAtCall] = useState(false);
  const [callMandatory, setCallMandatory] = useState(false);
  const [callWhen, setCallWhen] = useState("both");
  const [pickR, setPickR] = useState(""); const [pickD, setPickD] = useState(""); const [pickU, setPickU] = useState("");
  const [pickS, setPickS] = useState("");

  const toggleRole = (r) => setSelRoles((prev) =>
    prev.includes(r) ? prev.filter(x => x !== r) : [...prev, r]);

  useEffect(() => {
    if (!open) return;
    setSelRetailers(form.target_retailer_ids || []);
    setSelDistributors(form.target_distributor_ids || []);
    setSelUsers(form.target_user_ids || []);
    setSelRoles(form.applicable_role_codes || []);
    setSelSubZones(form.target_sub_zone_ids || []);
    api.get("/sub-zones").then(r => setSubZones(r.data.items || r.data || [])).catch(() => {});
    setShowAtCall(!!form.show_at_call);
    setCallMandatory(!!form.call_mandatory);
    setCallWhen(form.call_mandatory_when || "both");
    const rids = form.target_retailer_ids || [];
    Promise.all([
      // names of the already-picked retailers (the picker itself searches on the server)
      rids.length ? api.get("/retailers", { params: { ids: rids.join(","), page_size: 200 } })
        .then(r => setRetailers(r.data.items || [])).catch(() => {}) : Promise.resolve(),
      api.get("/distributors", { params: { page_size: 2000 } }).then(r => setDistributors(r.data.items || [])).catch(() => {}),
      api.get("/users", { params: { page_size: 500 } }).then(r => setUsers(r.data.items || [])).catch(() => {}),
    ]);
  }, [open, form]);

  const nameOfR = (id) => {
    const r = retailers.find(x => x.id === id);
    return r ? `${r.code || ""} — ${r.name || ""}`.trim() : id;
  };
  const nameOfD = (id) => {
    const d = distributors.find(x => x.id === id);
    return d ? `${d.code || ""} — ${d.name || ""}`.trim() : id;
  };
  const nameOfU = (id) => {
    const u = users.find(x => x.id === id);
    return u ? `${u.name || ""} · ${u.role_code || ""}`.trim() : id;
  };

  function addR() { if (pickR && !selRetailers.includes(pickR)) setSelRetailers([...selRetailers, pickR]); setPickR(""); }
  function addD() { if (pickD && !selDistributors.includes(pickD)) setSelDistributors([...selDistributors, pickD]); setPickD(""); }
  function addU() { if (pickU && !selUsers.includes(pickU)) setSelUsers([...selUsers, pickU]); setPickU(""); }

  function submit() {
    onSave({
      target_retailer_ids: selRetailers,
      target_distributor_ids: selDistributors,
      target_user_ids: selUsers,
      target_sub_zone_ids: selSubZones,
      applicable_role_codes: selRoles,
      show_at_call: showAtCall,
      call_mandatory: showAtCall && callMandatory,
      call_mandatory_when: callWhen,
    });
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="targeting-dialog">
        <DialogHeader>
          <DialogTitle>Outlet call &amp; targeting</DialogTitle>
          <DialogDescription>
            Choose whether this form opens during an outlet call (and whether the call can be completed
            without it), and who it applies to. If a list has entries, the visit must match;
            an empty list means "no restriction".
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 max-h-[65vh] overflow-y-auto pr-1">
          <div className="rounded-xl border border-neutral-200 p-3 space-y-3" data-testid="call-settings">
            <div className="flex items-start justify-between gap-3">
              <div>
                <Label className="text-sm">Show during the outlet call</Label>
                <p className="text-xs text-neutral-500 mt-0.5">
                  After the order is placed (or when the call is closed without an order) the user is taken straight to this form,
                  so it is not forgotten.
                </p>
              </div>
              <Switch checked={showAtCall} onCheckedChange={(v) => { setShowAtCall(v); if (!v) setCallMandatory(false); }}
                      data-testid="call-show-switch" />
            </div>
            {showAtCall && (
              <>
                <div className="flex items-start justify-between gap-3 border-t border-neutral-100 pt-3">
                  <div>
                    <Label className="text-sm">Mandatory to complete the call</Label>
                    <p className="text-xs text-neutral-500 mt-0.5">
                      The user cannot complete the call until this form is filled.
                    </p>
                  </div>
                  <Switch checked={callMandatory} onCheckedChange={setCallMandatory} data-testid="call-mandatory-switch" />
                </div>
                {callMandatory && (
                  <div className="space-y-1">
                    <Label className="text-xs">Mandatory for</Label>
                    <Select value={callWhen} onValueChange={setCallWhen}>
                      <SelectTrigger data-testid="call-when-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="both">All calls (with or without an order, shop closed too)</SelectItem>
                        <SelectItem value="with_order">Only calls where an order was placed</SelectItem>
                        <SelectItem value="without_order">Only calls without an order (incl. shop closed)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </>
            )}
          </div>
          <div data-testid="target-roles">
            <Label className="text-sm">Applicable Roles
              <span className="ml-2 text-xs text-neutral-400 font-normal">
                {selRoles.length === 0 ? "(empty = all)" : `${selRoles.length} selected`}
              </span>
            </Label>
            <div className="flex flex-wrap gap-2 mt-2">
              {TARGETING_ROLES.map((r) => (
                <button key={r} type="button"
                        data-testid={`target-role-chip-${r}`}
                        onClick={() => toggleRole(r)}
                        className={`text-xs px-3 py-1 rounded-full border transition ${
                          selRoles.includes(r)
                            ? "bg-emerald-600 text-white border-emerald-700"
                            : "bg-white text-neutral-600 border-neutral-300 hover:border-emerald-500"
                        }`}>
                  {r}
                </button>
              ))}
            </div>
          </div>
          <RetailerSearchSection picked={selRetailers} nameOf={nameOfR}
            onAdd={(r) => {
              setRetailers((prev) => (prev.some(x => x.id === r.id) ? prev : [...prev, r]));
              setSelRetailers((prev) => (prev.includes(r.id) ? prev : [...prev, r.id]));
            }}
            onRemove={(id) => setSelRetailers(selRetailers.filter(x => x !== id))}
            onClear={() => setSelRetailers([])} />
          <TargetSection label="Target Distributors" testid="target-distributors"
            picked={selDistributors} nameOf={nameOfD}
            pickValue={pickD} onPick={setPickD} onAdd={addD}
            onRemove={(id) => setSelDistributors(selDistributors.filter(x => x !== id))}
            options={distributors.map(d => ({ id: d.id, code: d.code, name: d.name }))}
            placeholder="Search distributor…" pickTestid="target-pick-distributor"
            addTestid="target-add-distributor"
            onSelectAll={() => setSelDistributors(distributors.map(d => d.id))}
            onClear={() => setSelDistributors([])} />
          <TargetSection label="Target Users" testid="target-users"
            picked={selUsers} nameOf={nameOfU}
            pickValue={pickU} onPick={setPickU} onAdd={addU}
            onRemove={(id) => setSelUsers(selUsers.filter(x => x !== id))}
            options={users.map(u => ({ id: u.id, name: u.name, code: u.role_code }))}
            placeholder="Search user…" pickTestid="target-pick-user"
            addTestid="target-add-user"
            onSelectAll={() => setSelUsers(users.filter(u => u.is_active !== false).map(u => u.id))}
            onClear={() => setSelUsers([])} />
          <TargetSection label="Target Sub-zones (all outlets, distributors and users of a sub-zone)" testid="target-subzones"
            picked={selSubZones}
            nameOf={(id) => { const z = subZones.find(x => x.id === id); return z ? `${z.code || ""} — ${z.name || ""}`.trim() : id; }}
            pickValue={pickS} onPick={setPickS}
            onAdd={() => { if (pickS && !selSubZones.includes(pickS)) setSelSubZones([...selSubZones, pickS]); setPickS(""); }}
            onRemove={(id) => setSelSubZones(selSubZones.filter(x => x !== id))}
            options={subZones.map(z => ({ id: z.id, code: z.code, name: z.name }))}
            placeholder="Search sub-zone…" pickTestid="target-pick-subzone" addTestid="target-add-subzone"
            onSelectAll={() => setSelSubZones(subZones.map(z => z.id))}
            onClear={() => setSelSubZones([])} />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid="targeting-save"
                  className="bg-emerald-600 hover:bg-emerald-700">
            <CheckCircle2 className="w-4 h-4 mr-1" /> Save targeting
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// Outlet picker that searches ALL outlets on the server (name, code or phone), because the
// old list only held the first 200 of tens of thousands.
function RetailerSearchSection({ picked, nameOf, onAdd, onRemove, onClear }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (q.trim().length < 2) { setResults([]); return undefined; }
    let live = true;
    const t = setTimeout(() => {
      setBusy(true);
      api.get("/retailers", { params: { q: q.trim(), page_size: 15, status: "active" } })
        .then((r) => { if (live) setResults(r.data.items || []); })
        .catch(() => { if (live) setResults([]); })
        .finally(() => { if (live) setBusy(false); });
    }, 350);
    return () => { live = false; clearTimeout(t); };
  }, [q]);
  return (
    <div data-testid="target-retailers">
      <Label className="text-sm">Target Retailers
        <span className="ml-2 text-xs text-neutral-400 font-normal">
          {picked.length === 0 ? "(no restriction)" : `${picked.length} selected`}
        </span>
      </Label>
      <div className="mt-1 flex items-center gap-2 text-xs">
        <span className={"rounded-full border px-2.5 py-0.5 font-semibold " + (picked.length === 0 ? "bg-emerald-600 text-white border-emerald-700" : "bg-white text-neutral-600 border-neutral-300")}
              data-testid="target-retailers-all">All retailers</span>
        <span className="text-neutral-500">
          {picked.length === 0 ? "— the form applies to every outlet (use Sub-zones / Distributors to narrow it)." : (
            <button type="button" className="text-[#6D2C91] underline" onClick={onClear}>Clear and apply to all retailers</button>)}
        </span>
      </div>
      <Input value={q} onChange={(e) => setQ(e.target.value)} className="mt-1"
             placeholder="Or pick specific outlets: type outlet name, code or phone…" data-testid="target-search-retailer" />
      {q.trim().length >= 2 && (
        <div className="mt-1 rounded-lg border border-neutral-200 max-h-44 overflow-y-auto divide-y">
          {busy && <div className="px-3 py-2 text-xs text-neutral-400">Searching…</div>}
          {!busy && results.length === 0 && <div className="px-3 py-2 text-xs text-neutral-400">No outlet found.</div>}
          {results.map((r) => (
            <button key={r.id} type="button" onClick={() => { onAdd(r); setQ(""); }}
                    disabled={picked.includes(r.id)}
                    className="w-full text-left px-3 py-1.5 text-xs hover:bg-neutral-50 disabled:opacity-40">
              {r.code} — {r.name}{r.address ? <span className="text-neutral-400"> · {String(r.address).slice(0, 40)}</span> : null}
            </button>
          ))}
        </div>
      )}
      {picked.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-2">
          {picked.map(id => (
            <Badge key={id} variant="outline"
                   className="bg-emerald-50 border-emerald-200 text-emerald-800 pl-2 pr-1 py-1">
              <span className="text-xs">{nameOf(id)}</span>
              <button type="button" onClick={() => onRemove(id)} className="ml-1 text-emerald-700 hover:text-red-600">
                <X className="w-3 h-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
}


function TargetSection({ label, testid, picked, nameOf, pickValue, onPick, onAdd,
                          onRemove, options, placeholder, pickTestid, addTestid, onSelectAll, onClear }) {
  return (
    <div data-testid={testid}>
      <Label className="text-sm">{label}
        <span className="ml-2 text-xs text-neutral-400 font-normal">
          {picked.length === 0 ? "(no restriction)" : `${picked.length} selected`}
        </span>
        {onSelectAll && (
          <span className="ml-3 text-xs font-normal">
            <button type="button" className="text-[#6D2C91] underline" onClick={onSelectAll} data-testid={`${testid}-select-all`}>
              Select all ({options.length})
            </button>
            {picked.length > 0 && onClear && (
              <button type="button" className="ml-2 text-neutral-500 underline" onClick={onClear}>Clear</button>
            )}
          </span>
        )}
      </Label>
      <div className="flex gap-2 mt-1">
        <div className="flex-1">
          <SearchableSelect value={pickValue} onChange={onPick} options={options}
                              placeholder={placeholder} testid={pickTestid} />
        </div>
        <Button size="sm" variant="outline" onClick={onAdd} disabled={!pickValue}
                data-testid={addTestid}>
          Add
        </Button>
      </div>
      {picked.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-2">
          {picked.map(id => (
            <Badge key={id} variant="outline"
                   className="bg-emerald-50 border-emerald-200 text-emerald-800 pl-2 pr-1 py-1">
              <span className="text-xs">{nameOf(id)}</span>
              <button type="button" onClick={() => onRemove(id)}
                      className="ml-1 text-emerald-700 hover:text-red-600"
                      data-testid={`remove-target-${id}`}>
                <X className="w-3 h-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
}
